// What the app does when the API answers 401 — one place, used by the axios
// response interceptor (src/api.ts) and the route error boundary
// (src/components/BoundaryFallback.tsx).
//
// Before this module a 401 signed the session out and still rejected to the
// caller, with no refresh attempt and no route to the login screen. On a page
// load with a token the backend no longer accepted, that rejection reached the
// page, the page threw, and the user saw "Oops! Something's gone wrong" with the
// nav still up (g2, 2026-10-11 23:31Z: /v6/users/me, /v6/config and the
// notification count all 401 on boot). An expired session is not a crash. It
// gets one forced refresh. If that fails, the user goes to the login screen and
// comes back to the page they were on.

import { refreshSession, signOutSession, startSignIn } from "./session";

const RETURN_KEY = "aeronet.auth.returnTo";
// A remembered page older than this is from an abandoned login, not this one.
const RETURN_TTL_MS = 30 * 60 * 1000;
// Requests already in flight with the old token come back 401 after the
// refresh that fixed them. Inside this window they retry on the new token
// instead of each refreshing again.
const RECENT_REFRESH_MS = 10 * 1000;
// The router basename (index.tsx `<BrowserRouter basename="app">`).
const BASENAME = "/app";

let inflight: Promise<boolean> | null = null;
let refreshedAt = 0;
let lost = false;

/**
 * Refresh the session once for every 401 that arrives together. Boot fires
 * /users/me, /config and the notification count at once; they share one
 * refresh rather than racing three.
 */
export function recoverSession(): Promise<boolean> {
  if (lost) return Promise.resolve(false);
  if (Date.now() - refreshedAt < RECENT_REFRESH_MS) return Promise.resolve(true);
  if (!inflight) {
    inflight = refreshSession()
      .then((ok) => {
        if (ok) refreshedAt = Date.now();
        return ok;
      })
      .catch(() => false)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** True once the session is known dead and the login redirect has started. */
export function isAuthLost(): boolean {
  return lost;
}

/**
 * The session cannot be recovered: remember where the user was, clear the
 * session, and send them to the login screen. Safe to call any number of
 * times; only the first call acts.
 */
export function sessionLost(): void {
  if (lost) return;
  lost = true;
  rememberReturnTo();
  // signOutSession() on a hosted-UI Cognito session redirects through the
  // Cognito /logout endpoint (which lands on the login screen) and does not
  // resolve. When it resolves it did NOT navigate (no hosted-UI session, or
  // GCIP), so startSignIn() takes the user to the login screen.
  signOutSession()
    .catch(() => {})
    .then(() => startSignIn())
    .catch(() => {});
}

function currentPath(): string {
  const { pathname, search, hash } = window.location;
  const inApp = pathname === BASENAME || pathname.startsWith(`${BASENAME}/`);
  const path = inApp ? pathname.slice(BASENAME.length) || "/" : pathname;
  return `${path}${search}${hash}`;
}

function isAppRoot(path: string): boolean {
  return path === "/" || path.startsWith("/?") || path.startsWith("/#");
}

/** Remember the current page (router-relative) for after the login round trip. */
export function rememberReturnTo(): void {
  const path = currentPath();
  if (isAppRoot(path)) return;
  try {
    sessionStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* storage disabled: the user lands on the dashboard instead */
  }
}

/**
 * The page to go back to after sign-in, once. Null when there is none, it is
 * stale, or it is not a plain in-app path.
 */
export function takeReturnTo(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== "string" || typeof at !== "number") return null;
    if (Date.now() - at > RETURN_TTL_MS) return null;
    // An in-app path only: never a protocol-relative or absolute URL.
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}

/** Test-only: module state survives between tests otherwise. */
export function __resetAuthRecoveryForTests(): void {
  inflight = null;
  refreshedAt = 0;
  lost = false;
}
